import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CustomField,
  CustomFieldType,
  FieldRenderConfig,
  CustomFieldsResponse,
  CustomFieldValidationRule,
} from '../models/custom-fields.model';
import { IntegrationService } from './Integration/integration.service';
import { Observable } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';

/**
 * Custom Field Renderer Service
 * Handles the creation and rendering logic for dynamic custom fields
 * Follows Angular best practices and project patterns
 */
@Injectable({
  providedIn: 'root',
})
export class CustomFieldRendererService {
  // Flag to enable/disable debugging logs
  // TEMPORARY: Enabled for debugging custom fields issue
  private enableDebugLogs = false;

  // Registry of custom fields per form to enable option lookups for dependencies
  private formFieldsRegistry = new WeakMap<FormGroup, Map<string, CustomField>>();
  // External adapters for fields that are not represented as form controls
  private externalAdapters = new WeakMap<FormGroup, Map<string, ExternalFieldAdapter>>();
  // Track related_fields effects to allow proper cleanup on selection changes
  private relatedFieldsState = new WeakMap<
    FormGroup,
    {
      lockCounts: Map<string, number>; // fieldName -> number of active locks (disabled sources)
      parentToTargets: Map<string, Set<string>>; // parentFieldName -> set of target field names affected
      syntheticByParent: Map<string, Set<string>>; // parentFieldName -> set of synthetic field names created by related_fields
    }
  >();
  private calculatedFieldsState = new WeakMap<FormGroup, Set<string>>();
  constructor(
    private integrationService: IntegrationService,
    private translocoService: TranslocoService,
  ) {}

  /**
   * Enable or disable debug logging
   * @param enabled - Whether to enable debug logs
   */
  setDebugLogging(enabled: boolean): void {
    this.enableDebugLogs = enabled;
  }

  /**
   * Get current debug logging state
   * @returns Current debug logging state
   */
  isDebugLoggingEnabled(): boolean {
    return this.enableDebugLogs;
  }

  /**
   * Internal logging method that respects the debug flag
   * @param args - Arguments to pass to console.log
   */
  private log(...args: any[]): void {
    if (this.enableDebugLogs) {
      console.log(...args);
    }
  }

  /**
   * Internal warning method that respects the debug flag
   * @param args - Arguments to pass to console.warn
   */
  private warn(...args: any[]): void {
    if (this.enableDebugLogs) {
      console.warn(...args);
    }
  }

  /**
   * Internal error method that respects the debug flag
   * @param args - Arguments to pass to console.error
   */
  private error(...args: any[]): void {
    if (this.enableDebugLogs) {
      console.error(...args);
    }
  }

  /**
   * Creates form controls for custom fields and adds them to the provided form
   * @param form - The form group to add controls to
   * @param customFields - Array of custom fields to create controls for
   * @param forceNullValues - If true, all fields will be initialized with null regardless of default values
   * @param skipDependencyCheck - If true, fields will be added regardless of dependency satisfaction (used for related_fields)
   */
  addCustomFieldsToForm(
    form: FormGroup,
    customFields: CustomField[],
    forceNullValues: boolean = false,
    skipDependencyCheck: boolean = false,
  ): void {
    // Ensure registry for this form exists
    if (!this.formFieldsRegistry.has(form)) {
      this.formFieldsRegistry.set(form, new Map<string, CustomField>());
    }
    const registry = this.formFieldsRegistry.get(form)!;

    customFields.forEach((field) => {
      this.log(`📝 [addCustomFieldsToForm] Processing field: "${field.name}"`, {
        type: field.type,
        read_only: field.read_only,
        depends_on: field.depends_on,
      });

      // Register this field for dependency resolution lookups
      registry.set(field.name, field);

      // Validate field configuration and log warning if needed
      this.validateFieldConfiguration(field);

      // If field has a dependency that is not satisfied, don't add it to the form
      // It will be added later when the dependency becomes satisfied
      // Skip this check if skipDependencyCheck is true (e.g., for related_fields)
      if (!skipDependencyCheck && field.depends_on && !this.isDependencySatisfied(form, field)) {
        this.warn(`  ⏭️ Skipping "${field.name}" - dependency not satisfied`, field.depends_on);
        return; // Skip adding this field
      }

      let validators = this.getValidatorsForField(field);

      // Extract validations for the current field
      if (field.validations && field.validations[field.name]) {
        field.validations[field.name].forEach((rule: CustomFieldValidationRule) => {
          if (rule.startsWith('min:')) {
            const minValue = Number(rule.split(':')[1]);
            if (field.type === 'text' || field.type === 'textarea') {
              validators.push(Validators.minLength(minValue));
            } else if (field.type === 'number') {
              validators.push(Validators.min(minValue));
            }
          }
          if (rule.startsWith('max:')) {
            const maxValue = Number(rule.split(':')[1]);
            if (field.type === 'text' || field.type === 'textarea') {
              validators.push(Validators.maxLength(maxValue));
            } else if (field.type === 'number') {
              validators.push(Validators.max(maxValue));
            }
          }
        });
      }

      // Use empty string if forceNullValues is true (better for mat-select display), otherwise use the default value
      const defaultValue = forceNullValues ? '' : this.getDefaultValue(field);
      const control = new FormControl(defaultValue, validators);

      // Handle read-only fields by disabling the control
      if (field.read_only) {
        control.disable();
      }

      form.addControl(field.name, control);
      this.log(`  ✅ Added control "${field.name}" to form with value:`, defaultValue);

      // Wire up related_fields behavior for select fields
      if (field.type === 'select') {
        // Apply initially if there is a default/auto value
        const initialValue = control.value;
        if (initialValue) {
          this.applyRelatedFieldsForSelection(form, field, initialValue);
        }
        // React to selection changes
        control.valueChanges.subscribe((val) => {
          this.applyRelatedFieldsForSelection(form, field, val);
        });
      }
    });

    this.setupUrgeScheduleVarianceCalculation(form);
  }

  private setupUrgeScheduleVarianceCalculation(form: FormGroup): void {
    const actualControl = form.get('actual_project_completion_percentage');
    const plannedControl = form.get('planned_project_completion_percentage');
    const scheduleVarianceControl = form.get('urge_schedule_variance');

    if (!actualControl || !plannedControl || !scheduleVarianceControl) {
      return;
    }

    if (!this.calculatedFieldsState.has(form)) {
      this.calculatedFieldsState.set(form, new Set<string>());
    }

    const calculatedFields = this.calculatedFieldsState.get(form)!;
    const fieldKey = 'urge_schedule_variance';

    if (!calculatedFields.has(fieldKey)) {
      calculatedFields.add(fieldKey);
      actualControl.valueChanges.subscribe(() => this.updateUrgeScheduleVariance(form));
      plannedControl.valueChanges.subscribe(() => this.updateUrgeScheduleVariance(form));
    }

    this.updateUrgeScheduleVariance(form);
  }

  private updateUrgeScheduleVariance(form: FormGroup): void {
    const actualControl = form.get('actual_project_completion_percentage');
    const plannedControl = form.get('planned_project_completion_percentage');
    const scheduleVarianceControl = form.get('urge_schedule_variance');

    if (!actualControl || !plannedControl || !scheduleVarianceControl) {
      return;
    }

    scheduleVarianceControl.setValue(
      this.calculateUrgeScheduleVariance(plannedControl.value, actualControl.value),
      { emitEvent: false },
    );
    scheduleVarianceControl.disable({ emitEvent: false });
  }

  private calculateUrgeScheduleVariance(planned: unknown, actual: unknown): string | null {
    const plannedValue = this.normalizePercentageValue(planned);
    const actualValue = this.normalizePercentageValue(actual);

    if (plannedValue === null || actualValue === null) {
      return null;
    }

    const variance = plannedValue - actualValue;
    return (Math.abs(variance) < 0.00005 ? 0 : variance).toFixed(4);
  }

  private normalizePercentageValue(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).replace(/[% ,]/g, '').trim();

    if (normalized === '') {
      return null;
    }

    const numericValue = Number(normalized);

    return Number.isFinite(numericValue) ? numericValue : null;
  }

  /**
   * Build validators for a field based on its type and required flag
   */
  private getValidatorsForField(field: CustomField) {
    const validators = [] as any[];

    if (field.required) {
      // Use Validators.required which treats empty strings as invalid
      validators.push(Validators.required);

      // Add additional validation to ensure empty strings are caught for select fields
      if (field.type === 'select') {
        validators.push((control: any) => {
          const value = control.value;
          // Treat empty string, null, and undefined as invalid for required select fields
          if (value === '' || value === null || value === undefined) {
            return { required: true };
          }
          return null;
        });
      }
    }

    switch (field.type) {
      case 'number':
        validators.push(Validators.pattern(/^\d+(\.\d+)?$/));
        break;
      case 'text':
        if (field.required) {
          validators.push(Validators.minLength(1));
        }
        break;
    }

    return validators;
  }

  /**
   * Returns true if a field's dependency (if any) is satisfied by the current form state
   */
  isDependencySatisfied(
    form: FormGroup & {
      metadata?: {
        categories?: Array<{
          label: string;
          value: string | number;
          children?: any[];
        }>;
      };
    },
    field: CustomField,
  ): boolean {
    if (!field.depends_on) {
      return true;
    }

    const parentControl = this.resolveDependencyControl(form, field.depends_on.field);
    const parentValue = parentControl?.value;
    const allowedValues = this.normalizeDependencyValues(field.depends_on.value);

    const matchesAllowed = (val: any): boolean => {
      if (val === null || val === undefined || val === '') {
        return false;
      }

      return allowedValues.includes(this.normalizeDependencyValue(val));
    };

    let result: boolean;
    if (Array.isArray(parentValue)) {
      result = parentValue.some((v) => matchesAllowed(v));
    } else {
      result = matchesAllowed(parentValue);
    }

    return result;
  }

  private normalizeDependencyValues(value: unknown): string[] {
    const rawValues = Array.isArray(value) ? value : String(value ?? '').split(',');

    return rawValues
      .map((item) => this.normalizeDependencyValue(item))
      .filter((item) => item.length > 0);
  }

  private normalizeDependencyValue(value: unknown): string {
    return String(value).trim().toLowerCase();
  }

  /**
   * Try to find a control for a dependency, supporting simple aliasing like category->categories
   */
  private resolveDependencyControl(form: FormGroup, name: string) {
    return form.get(name);
  }
  /**
   * Applies enable/disable and validator changes for a field based on dependency state
   * If dependency is not satisfied, removes the control from the form
   * If dependency is satisfied, adds the control back if it doesn't exist
   */
  applyDependencyState(form: FormGroup, field: CustomField): void {
    const active = this.isDependencySatisfied(form, field);
    const controlExists = form.contains(field.name);

    if (active) {
      // Dependency is satisfied - field should be visible and validated
      if (!controlExists) {
        // Control doesn't exist, add it
        const defaultValue = this.getDefaultValue(field);
        const validators = this.getValidatorsForField(field);
        const control = new FormControl(defaultValue, validators);
        form.addControl(field.name, control);
      } else {
        // Control exists, just ensure it's enabled with proper validators
        const control = form.get(field.name);
        if (control) {
          control.enable();
          control.setValidators(this.getValidatorsForField(field));
          control.updateValueAndValidity({ emitEvent: false });
        }
      }
    } else {
      // Dependency is NOT satisfied - field should not be in the form
      if (controlExists) {
        // Remove the control from the form completely
        form.removeControl(field.name);

        // Also remove any child fields this field might have
        // This ensures that nested dependent fields are also cleaned up
        this.removeAllChildFields(form, field);
      }
    }
  }

  /**
   * Validates field configuration and logs warnings for invalid setups
   * @param field - The custom field to validate
   */
  private validateFieldConfiguration(field: CustomField): void {
    // Check if field is read_only and required but doesn't have an auto_select value
    if (field.read_only && field.required && field.type === 'select') {
      if (!field.auto_select || field.auto_select === '') {
        this.warn(
          `⚠️ [CustomFieldRenderer] Field "${field.name}" (${field.label}) is read_only and required but does not have an auto_select value. ` +
            `This may cause validation issues as users cannot modify the field.`,
          { field },
        );
      }
    }
  }

  /**
   * Gets the default value for a custom field based on its type
   * @param field - The custom field
   * @returns The default value
   */
  private getDefaultValue(field: CustomField): any {
    // For select fields, check auto_select first
    if (field.type === 'select' && field.auto_select) {
      return field.auto_select;
    }

    if (field.default !== null && field.default !== undefined) {
      return field.default;
    }

    switch (field.type) {
      case 'text':
      case 'textarea':
        return '';
      case 'number':
        return null;
      case 'date':
      case 'datetime':
        return null;
      case 'select':
        // Use empty string for select fields for better mat-select compatibility
        return '';
      case 'boolean':
        return false;
      default:
        return null;
    }
  }

  /**
   * Register an external adapter for a field that is not represented directly as a FormControl.
   * This is used to keep UI bindings (e.g., selectedCategories) in sync when related_fields are applied.
   */
  registerExternalFieldAdapter(
    form: FormGroup,
    fieldName: string,
    adapter: ExternalFieldAdapter,
  ): void {
    if (!this.externalAdapters.has(form)) {
      this.externalAdapters.set(form, new Map<string, ExternalFieldAdapter>());
    }
    this.externalAdapters.get(form)!.set(fieldName, adapter);
  }

  /** Unregister a previously registered external adapter. */
  unregisterExternalFieldAdapter(form: FormGroup, fieldName: string): void {
    this.externalAdapters.get(form)?.delete(fieldName);
  }

  /** Ensure related fields state container exists */
  private getRelatedState(form: FormGroup) {
    if (!this.relatedFieldsState.has(form)) {
      this.relatedFieldsState.set(form, {
        lockCounts: new Map<string, number>(),
        parentToTargets: new Map<string, Set<string>>(),
        syntheticByParent: new Map<string, Set<string>>(),
      });
    }
    return this.relatedFieldsState.get(form)!;
  }

  /**
   * Apply related_fields for a given select field and current selected value.
   * Clears previous related_fields applied by this parent first, then applies new ones.
   */
  private applyRelatedFieldsForSelection(
    form: FormGroup,
    parentField: CustomField,
    selectedValue: any,
  ): void {
    const state = this.getRelatedState(form);

    // Clear prior effects from this parent
    this.clearRelatedFieldsForParent(form, parentField.name);

    if (!parentField.options || !selectedValue) {
      return;
    }

    const selectedOption = parentField.options.find((opt) => opt.value === selectedValue);
    const related = (selectedOption as any)?.related_fields as
      | Array<{ field_name?: string; filed_name?: string; field_value: any; read_only?: boolean }>
      | undefined;
    if (!related || related.length === 0) {
      return;
    }

    const targetsForParent = new Set<string>();
    const syntheticForParent = new Set<string>();

    this.log('related fields', related);
    related.forEach((ref) => {
      const targetKey = (ref.field_name ?? (ref as any).filed_name) as string;
      const targetValue = ref.field_value;
      const isReadOnly = ref.read_only ?? false; // Default to false if not specified
      if (!targetKey) {
        this.warn('⚠️ [CustomFieldRenderer] Related field has no field_name or filed_name:', ref);
        return;
      }

      this.log(
        `🔍 [CustomFieldRenderer] Processing related field: "${targetKey}" with value:`,
        targetValue,
        `read_only: ${isReadOnly}`,
      );

      // Try direct form control first
      let control = form.get(targetKey);
      let effectiveTargetKey = targetKey;

      if (!control) {
        this.log(`  → No existing control found for "${targetKey}"`);

        // Try to find a registered custom field by label or name and add its control if missing
        const registry = this.formFieldsRegistry.get(form);
        const registeredByName = registry?.get(targetKey);
        let registeredByLabel: CustomField | undefined;
        if (!registeredByName && registry) {
          for (const f of Array.from(registry.values())) {
            if (f.label === targetKey) {
              registeredByLabel = f;
              break;
            }
          }
        }
        const registered = registeredByName || registeredByLabel;
        if (registered) {
          this.log(`  → Found registered field:`, registered.name);
          effectiveTargetKey = registered.name;
          if (!form.contains(effectiveTargetKey)) {
            this.log(`  → Adding registered field to form (bypassing dependency check)`);
            // Create a copy of the field without depends_on so it bypasses shouldRender() check in template
            // This is necessary because related_fields explicitly requires this field to be shown
            // regardless of its original dependency configuration
            const fieldCopy = { ...registered, depends_on: null, default: targetValue };
            this.log(`  → Removed depends_on from field copy to ensure rendering`);
            this.addCustomFieldsToForm(form, [fieldCopy], false, true); // Skip dependency check for related fields
            // Update registry with the modified field so the renderer component sees the updated version
            registry.set(effectiveTargetKey, fieldCopy);
          }
          control = form.get(effectiveTargetKey) as any;
        }
      } else {
        this.log(`  → Found existing control for "${targetKey}"`);
      }

      if (!control) {
        // Check for external adapter (non-form binding)
        const adapter = this.externalAdapters.get(form)?.get(targetKey);
        if (adapter) {
          this.log(`  → Found external adapter for "${targetKey}"`);
          try {
            adapter.setValue(targetValue);
            if (adapter.setDisabled) adapter.setDisabled(isReadOnly);
          } catch (e) {
            this.warn(
              `⚠️ [CustomFieldRenderer] Failed to set external adapter value for "${targetKey}"`,
              e,
            );
          }
          // Track lock and target only if read-only
          if (isReadOnly) {
            const newCount = (state.lockCounts.get(targetKey) || 0) + 1;
            state.lockCounts.set(targetKey, newCount);
          }
          targetsForParent.add(targetKey);
          return;
        }

        // Create a synthetic custom field if nothing found (may be read-only or editable based on API)
        this.log(`  → Creating synthetic field for "${targetKey}"`);

        // Detect field type from value
        const fieldType = this.detectFieldType(targetValue);
        this.log(`  → Detected field type: "${fieldType}"`);

        const syntheticField: CustomField = {
          name: targetKey,
          label: this.formatFieldNameAsLabel(targetKey),
          type: fieldType,
          required: false,
          options: [],
          placeholder: '',
          default: null,
          read_only: isReadOnly,
          auto_select: '',
          depends_on: null,
          validations: {},
        } as any;

        this.log(`  → Synthetic field config:`, syntheticField);
        this.addCustomFieldsToForm(form, [syntheticField], false, true); // Skip dependency check for synthetic related fields
        control = form.get(targetKey) as any;

        if (control) {
          this.log(`  ✅ Synthetic field created successfully for "${targetKey}"`);

          // Convert values based on field type for synthetic fields
          let convertedValue = targetValue;
          if (fieldType === 'date' || fieldType === 'datetime') {
            convertedValue = this.convertDateValueForInput(targetValue, fieldType);
          } else if (
            fieldType === 'number' &&
            targetValue !== null &&
            targetValue !== undefined &&
            targetValue !== ''
          ) {
            // Convert string numbers to actual numbers for number fields
            const numValue = Number(targetValue);
            if (!isNaN(numValue)) {
              convertedValue = numValue;
            }
          }

          control.setValue(convertedValue, { emitEvent: true });
          if (isReadOnly) {
            control.disable({ emitEvent: false });
            const newCount = (state.lockCounts.get(targetKey) || 0) + 1;
            state.lockCounts.set(targetKey, newCount);
          }
          targetsForParent.add(targetKey);
          syntheticForParent.add(targetKey);
        } else {
          this.error(
            `  ❌ Failed to create synthetic field for "${targetKey}" - control not found after adding to form`,
          );
        }
        return;
      }

      // We have a form control: set value and disable (lock)
      // Handle multi-select controls by coercing scalar targetValue into an array when needed
      const currentControlValue = control.value;
      let valueToSet =
        Array.isArray(currentControlValue) && !Array.isArray(targetValue)
          ? [targetValue]
          : targetValue;

      // Convert values based on registered field type
      const registeredField = this.formFieldsRegistry.get(form)?.get(effectiveTargetKey);
      if (registeredField) {
        // Convert date/datetime strings to proper format
        if (registeredField.type === 'date' || registeredField.type === 'datetime') {
          valueToSet = this.convertDateValueForInput(valueToSet, registeredField.type);
        }
        // Convert string numbers to actual numbers for number fields
        else if (
          registeredField.type === 'number' &&
          valueToSet !== null &&
          valueToSet !== undefined &&
          valueToSet !== ''
        ) {
          const numValue = Number(valueToSet);
          if (!isNaN(numValue)) {
            valueToSet = numValue;
          }
        }
      }

      try {
        control.setValue(valueToSet, { emitEvent: true });
      } catch (e) {
        // Fallback without emitEvent in case of type mismatch
        control.setValue(valueToSet);
      }
      if (isReadOnly) {
        control.disable({ emitEvent: false });
        const newCount = (state.lockCounts.get(effectiveTargetKey) || 0) + 1;
        state.lockCounts.set(effectiveTargetKey, newCount);
      }
      targetsForParent.add(effectiveTargetKey);
    });

    // Save per-parent state
    state.parentToTargets.set(parentField.name, targetsForParent);
    state.syntheticByParent.set(parentField.name, syntheticForParent);

    // Update validity once after batch
    form.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Clear related_fields effects previously applied by a given parent field.
   */
  private clearRelatedFieldsForParent(form: FormGroup, parentFieldName: string): void {
    const state = this.getRelatedState(form);
    const targets = state.parentToTargets.get(parentFieldName);
    const synthetic = state.syntheticByParent.get(parentFieldName);
    if (!targets || targets.size === 0) {
      return;
    }

    targets.forEach((targetKey) => {
      // Decrement lock and re-enable when no more locks
      const current = state.lockCounts.get(targetKey) || 0;
      const next = Math.max(0, current - 1);
      state.lockCounts.set(targetKey, next);

      if (next === 0) {
        const control = form.get(targetKey);
        const fieldDefinition = this.formFieldsRegistry.get(form)?.get(targetKey);
        if (control) {
          control.setValue(fieldDefinition ? this.getDefaultValue(fieldDefinition) : null, {
            emitEvent: false,
          });

          if (fieldDefinition?.read_only) {
            control.disable({ emitEvent: false });
          } else {
            control.enable({ emitEvent: false });
          }
        }
        const adapter = this.externalAdapters.get(form)?.get(targetKey);
        if (adapter && adapter.setDisabled) {
          try {
            adapter.setDisabled(false);
          } catch {}
        }
      }

      // Remove synthetic field if we created it for this parent
      if (synthetic && synthetic.has(targetKey)) {
        if (form.contains(targetKey)) {
          form.removeControl(targetKey);
        }
        // Remove from registry if it exists
        this.formFieldsRegistry.get(form)?.delete(targetKey);
      }
    });

    state.parentToTargets.delete(parentFieldName);
    state.syntheticByParent.delete(parentFieldName);

    form.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Validates if a field type is supported
   * @param fieldType - The field type to validate
   * @returns True if supported, false otherwise
   */
  isSupportedFieldType(fieldType: string): fieldType is CustomFieldType {
    const supportedTypes: CustomFieldType[] = [
      'text',
      'textarea',
      'number',
      'date',
      'datetime',
      'select',
      'boolean',
    ];
    return supportedTypes.includes(fieldType as CustomFieldType);
  }

  /**
   * Gets CSS classes for field styling based on the project's design system
   * @param field - The custom field
   * @returns CSS classes string
   */
  getFieldCssClasses(field: CustomField): string {
    const baseClasses = 'w-full';

    switch (field.type) {
      case 'textarea':
        return baseClasses;
      case 'select':
        return baseClasses;
      default:
        return baseClasses;
    }
  }

  /**
   * Gets the Angular Material appearance for form fields
   * Following the project's existing pattern
   * @returns The appearance string
   */
  getMatFieldAppearance(): string {
    return 'fill';
  }

  /**
   * Prepares render configuration for a custom field
   * @param field - The custom field
   * @param value - Current value (optional)
   * @param disabled - Whether the field is disabled (optional)
   * @returns Field render configuration
   */
  prepareFieldConfig(field: CustomField, value?: any, disabled?: boolean): FieldRenderConfig {
    return {
      field,
      value: value !== undefined ? value : this.getDefaultValue(field),
      disabled: disabled || false,
      formControlName: field.name,
    };
  }

  /**
   * Formats date value to Y-m-d H:i:s format
   * @param value - The date value
   * @returns Formatted date string
   */
  formatDateForApi(value: any): string {
    if (!value) return '';

    const date = new Date(value);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Formats date value for display in date/datetime fields
   * @param value - The date value
   * @param fieldType - The field type
   * @returns Formatted date string
   */
  formatDateValue(value: any, fieldType: 'date' | 'datetime'): string {
    return this.formatDateForApi(value);
  }

  /**
   * Validates custom field values
   * @param field - The custom field
   * @param value - The value to validate
   * @returns Validation result
   */
  validateFieldValue(field: CustomField, value: any): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Helper to check if value is empty (null, undefined, or empty string)
    const isEmpty = (val: any) => val === null || val === undefined || val === '';

    // Required field validation - treat empty strings as missing values
    if (field.required && isEmpty(value)) {
      errors.push(this.translocoService.translate('validation.required', { field: field.label }));
    }

    // Type-specific validation - only validate non-empty values
    if (!isEmpty(value)) {
      switch (field.type) {
        case 'number':
          if (isNaN(Number(value))) {
            errors.push(
              this.translocoService.translate('validation.invalid_number', { field: field.label }),
            );
          }
          break;
        case 'date':
        case 'datetime':
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            errors.push(
              this.translocoService.translate('validation.invalid_date', { field: field.label }),
            );
          }
          break;
        case 'select':
          if (field.options && field.options.length > 0) {
            const validOptions = field.options.map((opt) => opt.value);
            if (!validOptions.includes(value)) {
              errors.push(
                this.translocoService.translate('validation.invalid_option', {
                  field: field.label,
                }),
              );
            }
          }
          break;
        case 'boolean':
          if (typeof value !== 'boolean') {
            errors.push(
              this.translocoService.translate('validation.invalid_boolean', {
                field: field.label,
              }),
            );
          }
          break;
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Load custom fields for a specific observation type and add them to the form
   * This method abstracts the common pattern used across multiple components
   *
   * @param observationType - The observation type to load custom fields for
   * @param form - The form group to add custom field controls to
   * @param onFieldsLoaded - Optional callback function called when fields are loaded
   * @param onError - Optional callback function called when an error occurs
   * @param observationId - Optional existing observation id. When provided, the BE
   *                       returns creation-locked fields (e.g. NCR Type) as read-only.
   *                       Leave undefined for the creation flow.
   * @returns Observable<CustomField[]> - Observable that emits the loaded custom fields
   */
  loadCustomFieldsForObservationType(
    observationType: string,
    form: FormGroup,
    onFieldsLoaded?: (fields: CustomField[]) => void,
    onError?: (error: any) => void,
    observationId?: string | number | null,
  ): Observable<CustomField[]> {
    return new Observable<CustomField[]>((observer) => {
      // Early return for note type or empty observation type
      if (!observationType || observationType === 'note') {
        const emptyFields: CustomField[] = [];
        observer.next(emptyFields);
        observer.complete();
        if (onFieldsLoaded) {
          onFieldsLoaded(emptyFields);
        }
        return;
      }

      const customFieldsSubscription = this.integrationService
        .getCustomFields(form.get('project_id')?.value, observationType, observationId)
        .subscribe({
          next: (response: CustomFieldsResponse) => {
            if (response && response.status === 'success' && response.data) {
              const customFields = response.data;
              this.addCustomFieldsToForm(form, customFields);

              observer.next(customFields);
              observer.complete();

              if (onFieldsLoaded) {
                onFieldsLoaded(customFields);
              }
            } else {
              const emptyFields: CustomField[] = [];
              observer.next(emptyFields);
              observer.complete();

              if (onFieldsLoaded) {
                onFieldsLoaded(emptyFields);
              }
            }
          },
          error: (error) => {
            const emptyFields: CustomField[] = [];
            console.error('📥 [loadCustomFieldsForObservationType] API error:', error);
            observer.next(emptyFields);
            observer.complete();

            if (onError) {
              onError(error);
            }
          },
        });

      return () => customFieldsSubscription.unsubscribe();
    });
  }

  /**
   * Get custom field values from the form
   * @param form - The form group containing custom field controls
   * @param customFields - Array of custom fields to extract values for
   * @returns Object containing field name-value pairs
   */
  getCustomFieldValues(form: FormGroup, customFields: CustomField[]): { [key: string]: any } {
    const customFieldValues: { [key: string]: any } = {};

    customFields.forEach((field) => {
      const control = form.get(field.name);
      if (control) {
        customFieldValues[field.name] = control.value;
      }
    });

    return customFieldValues;
  }

  /**
   * Finds the child field for a given parent field and selected value
   * @param parentField - The parent field
   * @param selectedValue - The selected value in the parent field
   * @returns The child field if found, null otherwise
   */
  findChildField(parentField: CustomField, selectedValue: string): CustomField | null {
    if (!parentField.options || parentField.options.length === 0) {
      return null;
    }

    const selectedOption = parentField.options.find((opt) => opt.value === selectedValue);
    const childField = selectedOption?.child || null;

    return childField;
  }

  /**
   * Gets all active child fields based on current form values
   * @param fields - Array of custom fields
   * @param form - The form group
   * @returns Array of active child fields
   */
  getActiveChildFields(fields: CustomField[], form: FormGroup): CustomField[] {
    const activeChildren: CustomField[] = [];

    fields.forEach((field) => {
      if (field.type === 'select' && field.options && field.options.length > 0) {
        const currentValue = form.get(field.name)?.value;
        if (currentValue) {
          const childField = this.findChildField(field, currentValue);
          if (childField) {
            activeChildren.push(childField);
            // Recursively get children of children
            const nestedChildren = this.getActiveChildFields([childField], form);
            activeChildren.push(...nestedChildren);
          }
        }
      }
    });

    return activeChildren;
  }

  /**
   * Removes child fields from the form
   * Also resets the parent field's value to prevent misleading UI state
   * @param form - The form group
   * @param childField - The child field to remove (will recursively remove its children too)
   */
  removeChildFieldsFromForm(form: FormGroup, childField: CustomField): void {
    // First, remove any children of this child field (recursive)
    if (childField.type === 'select' && childField.options) {
      childField.options.forEach((option) => {
        if (option.child) {
          this.removeChildFieldsFromForm(form, option.child);
        }
      });
    }

    // Clear the value of this field before removing it
    // This ensures parent fields don't show misleading values
    if (form.contains(childField.name)) {
      const control = form.get(childField.name);
      if (
        control &&
        control.value !== null &&
        control.value !== undefined &&
        control.value !== ''
      ) {
        control.setValue('', { emitEvent: true });
        control.updateValueAndValidity();
      }

      form.removeControl(childField.name);
    }
  }

  /**
   * Adds a child field to the form with a fresh, empty state
   * Ensures the field starts with null value (no cached values)
   * Exception: read_only fields with auto_select will use their auto_select value
   * @param form - The form group
   * @param childField - The child field to add
   */
  addChildFieldToForm(form: FormGroup, childField: CustomField): void {
    if (!form.contains(childField.name)) {
      // For read_only fields with auto_select, don't force null values
      // This allows auto_select to be used as the default value
      const shouldForceNull = !(childField.read_only && childField.auto_select);

      // Add the field to the form
      // If not read_only with auto_select, force null values for fresh state
      this.addCustomFieldsToForm(form, [childField], shouldForceNull);

      // Note: We don't recursively add nested descendants here
      // because the field value is null, so there are no descendants to add
    } else {
      // If it already exists (shouldn't happen but just in case), clear its value
      // Unless it's read_only with auto_select
      const control = form.get(childField.name);
      if (control) {
        if (childField.read_only && childField.auto_select) {
          control.setValue(childField.auto_select, { emitEvent: true });
        } else {
          control.setValue('', { emitEvent: true });
        }
        control.markAsUntouched();
        control.markAsPristine();
        control.updateValueAndValidity();
      }
    }
  }

  /**
   * Handles parent field value change - adds/removes child fields accordingly
   * ALWAYS removes ALL possible children first, then adds only the new ones
   * This ensures clean state even if old and new values have children with same names
   * @param form - The form group
   * @param parentField - The parent field that changed
   * @param newValue - The new value of the parent field
   * @param oldValue - The old value of the parent field (optional)
   */
  handleParentFieldChange(
    form: FormGroup,
    parentField: CustomField,
    newValue: string,
    oldValue?: string,
  ): void {
    // STEP 1: Remove ALL possible children (from ALL options), not just the old value's children
    // This ensures a clean state even if old and new values have children with the same names
    this.removeAllChildFields(form, parentField);

    // STEP 2: Add new child field if exists
    // The new child field will be added with proper validation
    if (newValue) {
      const newChildField = this.findChildField(parentField, newValue);
      if (newChildField) {
        this.addChildFieldToForm(form, newChildField);
      }
    }

    // STEP 3: Revalidate the entire form to ensure validation state is correct
    // Use emitEvent: false to avoid triggering excess change detection cycles
    // Angular will still detect the control additions/removals through its own mechanisms
    form.updateValueAndValidity({ emitEvent: false });

    // Log final form state for debugging
    this.logFormControls(form, `after handling parent field change for "${parentField.name}"`);
  }

  /**
   * Gets all child fields (nested) for a given field
   * @param field - The parent field
   * @returns Array of all nested child fields
   */
  getAllChildFields(field: CustomField): CustomField[] {
    const allChildren: CustomField[] = [];

    if (field.options && field.options.length > 0) {
      field.options.forEach((option) => {
        if (option.child) {
          allChildren.push(option.child);
          // Recursively get children of children
          const nestedChildren = this.getAllChildFields(option.child);
          allChildren.push(...nestedChildren);
        }
      });
    }

    return allChildren;
  }

  /**
   * Removes all child fields of a parent field from the form
   * Clears values before removal to prevent misleading UI state
   * @param form - The form group
   * @param parentField - The parent field
   */
  removeAllChildFields(form: FormGroup, parentField: CustomField): void {
    const allChildren = this.getAllChildFields(parentField);

    allChildren.forEach((child) => {
      if (form.contains(child.name)) {
        // Clear the value before removing
        const control = form.get(child.name);
        if (
          control &&
          control.value !== null &&
          control.value !== undefined &&
          control.value !== ''
        ) {
          control.setValue('', { emitEvent: true });
          control.updateValueAndValidity();
        }

        form.removeControl(child.name);
      }
    });
  }

  /**
   * Flattens nested custom fields into a single array (for iteration)
   * @param fields - Array of custom fields
   * @returns Flattened array including all nested fields
   */
  flattenCustomFields(fields: CustomField[]): CustomField[] {
    const flattened: CustomField[] = [];

    fields.forEach((field) => {
      flattened.push(field);
      const children = this.getAllChildFields(field);
      flattened.push(...children);
    });

    return flattened;
  }

  /**
   * Get all registered fields for a form
   * @param form - The form group
   * @returns Array of all custom fields registered for this form
   */
  getRegisteredFields(form: FormGroup): CustomField[] {
    const registry = this.formFieldsRegistry.get(form);
    if (!registry) {
      return [];
    }
    return Array.from(registry.values());
  }

  /**
   * Converts a field name to a human-readable label
   * Examples: "contract_reference" → "Contract Reference", "field_name" → "Field Name"
   */
  private formatFieldNameAsLabel(fieldName: string): string {
    return fieldName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Detects the appropriate field type based on the value
   * Used when creating synthetic fields from related_fields
   */
  private detectFieldType(value: any): CustomFieldType {
    if (value === null || value === undefined || value === '') {
      return 'text';
    }

    const stringValue = String(value);

    // Check for date formats: "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD"
    const dateTimePattern = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}$/;
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;

    if (dateTimePattern.test(stringValue)) {
      return 'datetime';
    }
    if (datePattern.test(stringValue)) {
      return 'date';
    }

    // Check for number
    if (!isNaN(Number(value)) && stringValue.trim() !== '') {
      return 'number';
    }

    // Check for boolean
    if (value === true || value === false || stringValue === 'true' || stringValue === 'false') {
      return 'boolean';
    }

    // Default to text
    return 'text';
  }

  /**
   * Converts a date value from backend format to the format expected by HTML date/datetime inputs
   * Backend: "2025-10-26 00:00:00" or "2025-10-26"
   * HTML datetime-local input: "2025-10-26T00:00"
   * HTML date input: "2025-10-26"
   */
  private convertDateValueForInput(value: any, fieldType: 'date' | 'datetime'): string {
    if (!value) return '';

    const stringValue = String(value);

    if (fieldType === 'datetime') {
      // Convert "2025-10-26 00:00:00" to "2025-10-26T00:00"
      const match = stringValue.match(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2})(:\d{2})?$/);
      if (match) {
        return `${match[1]}T${match[2]}`;
      }
    } else if (fieldType === 'date') {
      // Extract just the date part "2025-10-26"
      const match = stringValue.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match) {
        return match[1];
      }
    }

    return stringValue;
  }

  /**
   * Logs the current state of all form controls for debugging purposes
   * @param form - The form group to log
   * @param context - Context description for the log
   */
  private logFormControls(form: FormGroup, context: string): void {
    const controls = Object.keys(form.controls);
    const controlStates = controls.map((controlName) => {
      const control = form.get(controlName);
      const hasRequiredValidator = control?.hasError('required');
      const validators = control?.validator ? control.validator(control) : null;
      const isRequired =
        control?.hasError('required') || (validators && validators['required'] !== undefined);

      return {
        name: controlName,
        value: control?.value,
        enabled: control?.enabled,
        disabled: control?.disabled,
        valid: control?.valid,
        invalid: control?.invalid,
        required: isRequired,
        hasRequiredValidator: hasRequiredValidator,
        errors: control?.errors,
      };
    });
  }

  /**
   * Clears all custom field-related state for a form
   * This includes:
   * - All registered custom fields from the registry
   * - All related fields state (synthetic fields, lock counts, parent-to-targets mappings)
   * - All external field adapters
   * - All calculated field wiring state
   *
   * Use this method when you need to completely reset custom fields state,
   * such as when changing observation types.
   *
   * @param form - The form group to clear state for
   * @param customFieldsToRemove - Optional array of custom fields to remove. If not provided, all registered fields will be removed.
   */
  clearAllCustomFieldsState(form: FormGroup, customFieldsToRemove?: CustomField[]): void {
    const registry = this.formFieldsRegistry.get(form);

    // BUGFIX: If customFieldsToRemove is an empty array, fall back to registry
    // The || operator doesn't catch empty arrays, so we need an explicit check
    const fieldsToRemove =
      customFieldsToRemove && customFieldsToRemove.length > 0
        ? customFieldsToRemove
        : registry
        ? Array.from(registry.values())
        : [];

    this.log(`  → Found ${fieldsToRemove.length} custom fields to remove`);
    this.log(`  → customFieldsToRemove provided:`, customFieldsToRemove?.length ?? 'none');
    this.log(`  → Registry has:`, registry?.size ?? 0, 'fields');

    // 1. Remove all registered custom field controls from the form
    fieldsToRemove.forEach((field) => {
      if (form.contains(field.name)) {
        this.log(`  → Removing control: ${field.name}`);
        form.removeControl(field.name);
      }
    });

    // 2. Clear all related fields effects for each parent field
    // This handles synthetic fields and re-enables any locked fields
    if (registry) {
      const state = this.relatedFieldsState.get(form);
      if (state) {
        // Get all parent fields that have related_fields effects
        const parentFields = Array.from(state.parentToTargets.keys());
        this.log(`  → Found ${parentFields.length} parent fields with related_fields effects`);

        // Clear effects for each parent
        parentFields.forEach((parentFieldName) => {
          this.log(`  → Clearing related_fields effects for parent: ${parentFieldName}`);
          this.clearRelatedFieldsForParent(form, parentFieldName);
        });

        // Clear the entire state object to ensure no lingering references
        state.lockCounts.clear();
        state.parentToTargets.clear();
        state.syntheticByParent.clear();
      }
    }

    // 3. Clear the registry for this form
    if (registry) {
      this.log(`  → Clearing registry (${registry.size} entries)`);
      registry.clear();
    }

    // 4. Clear all external adapters for this form
    const adapters = this.externalAdapters.get(form);
    if (adapters) {
      this.log(`  → Clearing external adapters (${adapters.size} entries)`);
      adapters.clear();
    }

    // 5. Clear calculated field wiring state for this form
    this.calculatedFieldsState.delete(form);
  }

  /**
   * Get all fields that depend on a specific parent field
   * @param form - The form group
   * @param parentFieldName - The name of the parent field (e.g., 'categories')
   * @returns Array of fields that have depends_on pointing to this parent
   */
  getDependentFields(form: FormGroup, parentFieldName: string): CustomField[] {
    const registry = this.formFieldsRegistry.get(form);
    if (!registry) {
      return [];
    }

    const allFields = Array.from(registry.values());
    return allFields.filter(
      (field) => field.depends_on && field.depends_on.field === parentFieldName,
    );
  }

  /**
   * Get all fields that would be satisfied by a specific parent field value
   * This is useful for detecting which fields will become visible when a parent changes
   * @param form - The form group
   * @param parentFieldName - The name of the parent field (e.g., 'categories')
   * @param parentValue - The proposed value for the parent field
   * @returns Array of fields that would be satisfied by this parent value
   */
  getFieldsSatisfiedByParentValue(
    form: FormGroup,
    parentFieldName: string,
    parentValue: any,
  ): CustomField[] {
    const dependentFields = this.getDependentFields(form, parentFieldName);

    // Create a temporary form state to check satisfaction
    const tempForm = new FormGroup({});
    tempForm.addControl(parentFieldName, new FormControl(parentValue));

    return dependentFields.filter((field) => this.isDependencySatisfied(tempForm, field));
  }

  /**
   * Get required dependent fields that would be empty/invalid after a parent field change
   * This is the key method for detecting fields that need user attention
   * @param form - The form group
   * @param parentFieldName - The name of the parent field
   * @param newParentValue - The new value for the parent field
   * @returns Array of required fields that are empty/invalid and would be satisfied
   */
  getRequiredEmptyDependentFields(
    form: FormGroup,
    parentFieldName: string,
    newParentValue: any,
  ): CustomField[] {
    // Get all fields that would be satisfied by the new parent value
    const satisfiedFields = this.getFieldsSatisfiedByParentValue(
      form,
      parentFieldName,
      newParentValue,
    );

    // Filter to only required fields that are currently invalid or empty
    return satisfiedFields.filter((field) => {
      if (!field.required) {
        return false;
      }

      // Check if control exists
      const control = form.get(field.name);
      if (!control) {
        // Field doesn't exist yet, so it would be empty
        return true;
      }

      // Check if control is invalid (this includes required validation)
      // A required field that's empty will have invalid=true with required error
      if (control.invalid) {
        return true;
      }

      const value = control.value;
      // Additional check: ensure value is not empty even if validation passes
      // (handles edge cases where validator might not catch empty values)
      const isEmpty =
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);

      return isEmpty;
    });
  }

  /**
   * Check if all required dependent fields are satisfied for a parent field
   * This is used to determine if submit button should be enabled
   * @param form - The form group
   * @param parentFieldName - The name of the parent field
   * @param parentValue - The current/proposed value for the parent field
   * @returns True if all required dependent fields are valid, false otherwise
   */
  areRequiredDependentFieldsSatisfied(
    form: FormGroup,
    parentFieldName: string,
    parentValue: any,
  ): boolean {
    const requiredEmptyFields = this.getRequiredEmptyDependentFields(
      form,
      parentFieldName,
      parentValue,
    );
    return requiredEmptyFields.length === 0;
  }

  /**
   * Get human-readable message about unsatisfied dependent fields
   * @param form - The form group
   * @param parentFieldName - The name of the parent field
   * @param parentValue - The proposed value for the parent field
   * @returns Message explaining which fields are required
   */
  getUnsatisfiedDependentFieldsMessage(
    form: FormGroup,
    parentFieldName: string,
    parentValue: any,
  ): string {
    const requiredEmptyFields = this.getRequiredEmptyDependentFields(
      form,
      parentFieldName,
      parentValue,
    );

    if (requiredEmptyFields.length === 0) {
      return '';
    }

    const fieldLabels = requiredEmptyFields.map((field) => field.label).join(', ');
    return requiredEmptyFields.length === 1
      ? `Required field must be filled: ${fieldLabels}`
      : `Required fields must be filled: ${fieldLabels}`;
  }
}

/** Adapter for non-form bindings to be controlled by related_fields. */
export interface ExternalFieldAdapter {
  setValue: (value: any) => void;
  setDisabled?: (disabled: boolean) => void;
}
